import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getTicketHead, updateHead } from "../../api/settingApi";
import { useNotification } from "../../context/NotificationContext";

const ModifyHead = () => {
  const navigate = useNavigate();
  const notify = useNotification();
  const { id } = useParams(); // head ID from route

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    hasCheckList: false,
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Load Ticket Head
  useEffect(() => {
    const fetchHead = async () => {
      try {
        const res = await getTicketHead(id);
        console.log(res);
        setFormData({
          name: res.name || "",
          description: res.description || "",
          hasCheckList: res.hasCheckList || false,
        });
      } catch (err) {
        console.error(err);
        notify("error", "Failed to load Ticket Head");
      }
    };
    fetchHead();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess("");

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      await updateHead(id, formData);
      setSuccess("Ticket Head updated successfully!");
      notify("success", "Ticket Head updated successfully!");
      setTimeout(() => navigate("/settings"), 2000);
    } catch (err) {
      console.error(err);
      setErrors({ submit: err.response?.data?.message || "Update failed." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen">
      <form
        onSubmit={handleSubmit}
        className="bg-white p-10 rounded-lg shadow-lg w-full max-w-md border-t-8 border-green-500"
      >
        <h2 className="text-3xl font-extrabold mb-6 text-blue-800 text-center">
          Modify Head
        </h2>

        {errors.submit && (
          <div className="mb-4 bg-red-600 text-white border border-red-700 p-3 rounded shadow-md flex items-start space-x-2">
            <span className="font-bold">⚠️</span>
            <span>{errors.submit}</span>
          </div>
        )}
        {success && (
          <div className="mb-4 text-green-700 bg-green-100 border border-green-300 p-3 rounded">
            {success}
          </div>
        )}

        {/* Name */}
        <div className="mb-4">
          <label className="block text-blue-800 mb-2 font-medium">Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
            required
          />
          {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="block text-blue-800 mb-2 font-medium">Description</label>
          <input
            type="text"
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
          />
        </div>

        {/* Has Checklist */}
        <div className="mb-6 flex items-center">
          <input
            type="checkbox"
            id="hasCheckList"
            name="hasCheckList"
            checked={formData.hasCheckList}
            onChange={handleChange}
            className="h-4 w-4 text-green-600 border-blue-300 rounded focus:ring-green-500"
          />
          <label htmlFor="hasCheckList" className="ml-2 text-blue-800 font-medium">
            Has Check List
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-green-500 text-white font-semibold py-3 rounded hover:bg-green-600 transition disabled:opacity-50"
        >
          {loading ? "Updating..." : "Update"}
        </button>
      </form>
    </div>
  );
};

export default ModifyHead;
